import {
  createConvexAuthController,
  createConvexVueClient,
  recoverConvexSubscription,
  runConvexWithAuthRecovery,
  type ConvexAuthRecoveryResult,
  type ConvexAuthTokenFetcher,
  type ConvexVueClientLike,
  type RecoverConvexSubscriptionOptions,
  type RunConvexWithAuthRecoveryOptions,
} from "@shopexperts/convex-vue";
import type { FunctionReference } from "convex/server";
import { api } from "../../convex/_generated/api";
import { addConvexBreadcrumb, captureConvexError } from "@/v2Projects/telemetry";

let client: ConvexVueClientLike | null = null;
let authController: ReturnType<typeof createConvexAuthController> | null = null;
let removeFocusAuthCheck: (() => void) | null = null;

interface ConfigureProjectsConvexAuthOptions {
  fetchToken: ConvexAuthTokenFetcher;
  verifyApiAuth: () => Promise<boolean>;
  onLogout: (reason: string, error?: unknown) => void | Promise<void>;
  clearTokenCache?: () => void;
}

function getConvexUrl(): string {
  const url = import.meta.env.VITE_CONVEX_URL as string | undefined;
  if (!url) {
    throw new Error("Missing VITE_CONVEX_URL for Convex projects module");
  }
  return url;
}

export function getProjectsConvexClient(): ConvexVueClientLike {
  if (!client) {
    client = createConvexVueClient({ url: getConvexUrl() });
  }
  return client;
}

export function configureProjectsConvexAuth(options: ConfigureProjectsConvexAuthOptions) {
  removeFocusAuthCheck?.();
  authController?.clearAuth();
  authController = createConvexAuthController({
    client: getProjectsConvexClient(),
    fetchToken: async ({ forceRefreshToken }) => {
      addConvexBreadcrumb({
        operation: "convex_auth.fetch_token_started",
        level: "info",
        auth: { forceRefreshToken },
      });
      const token = await options.fetchToken({ forceRefreshToken });
      addConvexBreadcrumb({
        operation: "convex_auth.fetch_token_finished",
        level: token ? "info" : "warning",
        auth: { forceRefreshToken, isAuthenticated: Boolean(token) },
      });
      return token;
    },
    verifyApiAuth: options.verifyApiAuth,
    clearTokenCache: options.clearTokenCache,
    onLogout: options.onLogout,
    telemetry: {
      breadcrumb: addConvexBreadcrumb,
      error: captureConvexError,
    },
  });
  authController.applyAuth();
  removeFocusAuthCheck = authController.installFocusAuthCheck();
}

export async function refreshProjectsConvexAuth(forceRefreshToken = true): Promise<string | null> {
  if (!authController) {
    return null;
  }

  return await authController.fetchToken({ forceRefreshToken });
}

export async function recoverProjectsConvexAuth(context: {
  operation: string;
  error: unknown;
  functionName?: string;
  recoveryAttempt: number;
}): Promise<ConvexAuthRecoveryResult> {
  if (!authController) {
    return { didRecover: false, shouldLogout: false, reason: "token-refresh-failed" };
  }
  return await authController.recoverFromAuthError(context);
}

export async function recoverProjectsConvexSubscription(
  options: Omit<RecoverConvexSubscriptionOptions, "recoverAuth">,
) {
  return await recoverConvexSubscription({
    ...options,
    recoverAuth: recoverProjectsConvexAuth,
  });
}

export async function runProjectsConvexQuery<Query extends FunctionReference<"query">>(
  query: Query,
  args: Query["_args"],
  options: Omit<RunConvexWithAuthRecoveryOptions<Query["_returnType"]>, "run" | "recoverAuth">,
): Promise<Query["_returnType"]> {
  return await runConvexWithAuthRecovery({
    ...options,
    recoverAuth: recoverProjectsConvexAuth,
    run: () => getProjectsConvexClient().query(query, args),
  });
}

export async function runProjectsConvexMutation<Mutation extends FunctionReference<"mutation">>(
  mutation: Mutation,
  args: Mutation["_args"],
  options: Omit<RunConvexWithAuthRecoveryOptions<Mutation["_returnType"]>, "run" | "recoverAuth">,
): Promise<Mutation["_returnType"]> {
  return await runConvexWithAuthRecovery({
    ...options,
    recoverAuth: recoverProjectsConvexAuth,
    run: () => getProjectsConvexClient().mutation(mutation, args),
  });
}

export function restartProjectsConvexClientAuth() {
  authController?.applyAuth();
}

export function clearProjectsConvexAuth() {
  authController?.clearAuth();
}

export const projectFns = {
  listDirectRooms: api.chatrooms.listDirect,
  directUnreadCount: api.chatrooms.directUnreadCount,
  getDirectRoom: api.chatrooms.getDirect,
  markThreadRead: api.chat.mutations.markThreadRead,
  getProjectMessages: api.chat.queries.getProjectMessages,
  getProjectThreadId: api.chat.queries.getProjectThreadId,
  getThreadMessagesById: api.chat.queries.getThreadMessagesById,
  sendProjectMessage: api.chat.mutations.sendMessage,
  sendProjectNote: api.chat.mutations.sendNote,
  getUnreadNotificationsCount: api.notifications.countUnreadForViewer,
  listNotifications: api.notifications.listForViewer,
  markNotificationRead: api.notifications.markRead,
  dismissNotification: api.notifications.dismiss,
  markNotificationUnread: api.notifications.markUnread,
  markAllNotificationsRead: api.notifications.markAllRead,
} as const;

export function getConvexAuthInfo(): { userId: number; orgId: number; isAdmin: boolean } | null {
  const convexClient = getProjectsConvexClient();
  const auth = convexClient.getAuth?.();
  if (!auth?.decoded) return null;
  const { user_id, org_id, is_admin } = auth.decoded as Record<string, unknown>;
  if (typeof user_id !== "number" || typeof org_id !== "number") return null;
  return { userId: user_id, orgId: org_id, isAdmin: is_admin === true };
}
